// src/components/MallInitializer.tsx
//
// UserContext の shopUrl / Amazon設定 を読み取って MallContext.shops を初期化するブリッジ。
// AppShell の内側（MallProvider 配下）で1回だけマウントする。

import { useEffect, useRef } from "react";
import { useUserContext } from "../contexts/UserContext";
import { useMallContext } from "../contexts/MallContext";
import { shopRefFromShopUrl, shopRefFromSellerId, shopRefFromStoreId } from "../types/mall";
import { apiGetJson } from "../utils/apiClient";

function getAmazonStorageScope(): { tenantId: string; companyId: string } {
    if (typeof window === "undefined") {
        return { tenantId: "ecvision", companyId: "default" };
    }
    const tenantId = String(localStorage.getItem("selectedTenant") || "ecvision").trim() || "ecvision";
    const companyId = String(
        localStorage.getItem("selectedCompanyId") ||
        localStorage.getItem("companyId") ||
        localStorage.getItem("accountId") ||
        "default"
    ).trim() || "default";
    return { tenantId, companyId };
}

function getAmazonStorageKey(baseKey: "amazonSellerId" | "amazonSellerName" | "yahooStoreId" | "yahooStoreName"): string {
    const scope = getAmazonStorageScope();
    return `${baseKey}:${scope.tenantId}:${scope.companyId}`;
}

function getStoredAmazonValue(baseKey: "amazonSellerId" | "amazonSellerName" | "yahooStoreId" | "yahooStoreName"): string {
    if (typeof window === "undefined") return "";
    const scopedKey = getAmazonStorageKey(baseKey);
    return String(localStorage.getItem(scopedKey) || "").trim();
}

export default function MallInitializer() {
    const user = useUserContext();
    const { setShops, setEnabledMalls } = useMallContext();
    const requestKeyRef = useRef("");

    useEffect(() => {
        if (user.loading) return;

        const requestKey = `${String(user.tenantId || "")}|${String(user.companyId || "")}|${String(user.shopUrl || "")}`;
        if (requestKeyRef.current === requestKey) return;
        requestKeyRef.current = requestKey;

        const shops = [];

        // 楽天: UserContext.shopUrl から生成
        const rakutenShopUrl =
            user.shopUrl ||
            (typeof window !== "undefined"
                ? (localStorage.getItem("selectedShopUrl") || localStorage.getItem("shopUrl") || "")
                : "");

        if (rakutenShopUrl) {
            shops.push(shopRefFromShopUrl(rakutenShopUrl));
        }

        // Amazon: ローカルストレージに保存した sellerId があれば追加
        // (AmazonSettingsPage で保存する)
        if (typeof window !== "undefined") {
            const savedSellerId = getStoredAmazonValue("amazonSellerId");
            const savedSellerName = getStoredAmazonValue("amazonSellerName");
            if (savedSellerId) {
                shops.push(shopRefFromSellerId(savedSellerId, savedSellerName || savedSellerId));
            }

            // Yahoo: API応答前の初期リストに含めないと、Sidebar の
            // 「選択モールが連携済みリストに無ければ楽天へ戻す」フォールバックが
            // 再マウントのたびに発動して Yahoo 選択が失われる
            const savedStoreId = getStoredAmazonValue("yahooStoreId");
            const savedStoreName = getStoredAmazonValue("yahooStoreName");
            if (savedStoreId) {
                shops.push(shopRefFromStoreId(savedStoreId, savedStoreName || savedStoreId));
            }
        }

        setShops(shops);

        let cancelled = false;

        apiGetJson<{ enabledMalls?: string[]; shops?: Array<{ mall: string; mallShopId: string; displayName?: string }> }>("/api/company/shops")
            .then((json) => {
                if (cancelled || !json?.shops) return;

                const updatedShops = [];
                const apiRakuten = json.shops.find((s: any) => s.mall === "rakuten");
                const apiAmazon = json.shops.find((s: any) => s.mall === "amazon");
                const apiYahoo = json.shops.find((s: any) => s.mall === "yahoo");
                const enabledMalls = Array.isArray(json.enabledMalls)
                    ? json.enabledMalls.map((x) => String(x || "").toLowerCase())
                    : [];
                // 管理ツールの「利用モール」をコンテキストに反映（サイドバーの連携設定メニュー表示ゲート）
                setEnabledMalls(enabledMalls);
                const amazonEnabled = enabledMalls.includes("amazon");

                if (apiRakuten?.mallShopId) {
                    updatedShops.push(shopRefFromShopUrl(apiRakuten.mallShopId));
                } else if (rakutenShopUrl) {
                    updatedShops.push(shopRefFromShopUrl(rakutenShopUrl));
                }

                if (apiAmazon?.mallShopId) {
                    const sellerName = apiAmazon.displayName || apiAmazon.mallShopId;
                    updatedShops.push(shopRefFromSellerId(apiAmazon.mallShopId, sellerName));
                    // キャッシュ更新
                    localStorage.setItem(getAmazonStorageKey("amazonSellerId"), apiAmazon.mallShopId);
                    if (sellerName) localStorage.setItem(getAmazonStorageKey("amazonSellerName"), sellerName);
                } else if (amazonEnabled) {
                    const savedSellerId = getStoredAmazonValue("amazonSellerId");
                    const savedSellerName = getStoredAmazonValue("amazonSellerName");

                    if (savedSellerId) {
                        updatedShops.push(shopRefFromSellerId(savedSellerId, savedSellerName || savedSellerId));
                    } else {
                        // sellerId未設定でも「Amazon有効化済み」をUIに反映する
                        updatedShops.push(shopRefFromSellerId("__amazon_unconfigured__", "Amazon"));
                    }
                }

                if (apiYahoo?.mallShopId) {
                    const storeName = apiYahoo.displayName || apiYahoo.mallShopId;
                    updatedShops.push(shopRefFromStoreId(apiYahoo.mallShopId, storeName));
                    // キャッシュ更新（Amazon と同様。初期リスト用）
                    localStorage.setItem(getAmazonStorageKey("yahooStoreId"), apiYahoo.mallShopId);
                    if (storeName) localStorage.setItem(getAmazonStorageKey("yahooStoreName"), storeName);
                } else {
                    // 連携解除・利用モールOFF時はキャッシュも消す
                    localStorage.removeItem(getAmazonStorageKey("yahooStoreId"));
                    localStorage.removeItem(getAmazonStorageKey("yahooStoreName"));
                }

                if (updatedShops.length > 0) {
                    setShops(updatedShops);
                }
            })
            .catch(() => {
                // 初回はローカル fallback を維持
            });

        return () => {
            cancelled = true;
        };
    }, [user.loading, user.tenantId, user.companyId, user.shopUrl, setShops, setEnabledMalls]);

    return null;
}
